import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  ButtonsWrapper,
  CheckboxSelect,
  Form,
  SelectInput,
  TextInput,
} from '@thinknet-gmbh/platform-common-ui';

import { DEFAULT_NAME_FIELD_REGEX, EmployeeCreationTypeEnum } from '@thinknet-gmbh/platform-common';

import { useGetCurrentCompany } from 'src/hooks/companies/get-current-company.hook';
import { useGetAllEmployeesForList } from 'src/hooks/employees/get-all-employees-for-list.hook';
import { useGetAllPlatformCompanies } from 'src/hooks/companies/get-all-platform-companies.hook';
import { useGetJobLocationsByDepartment } from 'src/hooks/jobs/get-job-locations-by-department.hook';
import { useGetEmployeeQuestionnaireSettingByIds } from 'src/hooks/employee-questionnaire-settings/get-employee-questionnaire-settings-by-ids.hook';

import { resetFields } from './employee-by-questionnaire-form.helpers';
import { FieldsRow, MainWrapper } from './employee-by-questionnaire-form.styles';

import { ROUTES } from 'src/components/app/app-routes.constants';
import { ICreateEmployeeFormProps } from '../../create-employee-by-questionnaire.interfaces';

const EmployeeByQuestionnaireForm = ({
  initialValues,
  onFinish,
  loading,
}: ICreateEmployeeFormProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('employee');
  const [form] = Form.useForm();
  const [departmentId, setDepartmentId] = useState<string>('');
  const [questionnaireSettingIds, setQuestionnaireSettingIds] = useState<string[]>([]);
  const { companies } = useGetAllPlatformCompanies();
  const { company } = useGetCurrentCompany();
  const { jobs } = useGetJobLocationsByDepartment(
    departmentId || (initialValues && initialValues.departmentId)
  );

  const { questionnaireSetting } = useGetEmployeeQuestionnaireSettingByIds(questionnaireSettingIds);
  const { employees } = useGetAllEmployeesForList({
    filter: { companyId: company._id, accountActive: true, includeSelf: true },
  });

  const handleSelect = (field: string) => {
    resetFields(field, form);
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onFinish}
      scrollToFirstError
      initialValues={initialValues}
    >
      <MainWrapper>
        <FieldsRow>
          <Form.Item
            name={'creationType'}
            label={t('employee:questionnaire.labels.creationType')}
            rules={[
              {
                required: true,
                message: t('employee:questionnaire.validation.creationTypeRequired'),
              },
            ]}
          >
            <SelectInput placeholder="Questionnaire">
              <SelectInput.Option
                value={EmployeeCreationTypeEnum.questionnaire}
                key={EmployeeCreationTypeEnum.questionnaire}
                title={t('employee:questionnaire.labels.creationType')}
              >
                {t(
                  `employee:questionnaire.labels.creationTypeOptions.${EmployeeCreationTypeEnum.questionnaire}`
                )}
              </SelectInput.Option>
            </SelectInput>
          </Form.Item>
        </FieldsRow>

        <FieldsRow>
          <Form.Item
            name={'firstName'}
            label={t('common:labels.firstName')}
            rules={[
              { required: true, message: t('common:validation.error.firstNameRequired') },
              {
                pattern: DEFAULT_NAME_FIELD_REGEX,
                message: t('common:validation.error.incorrectFormat'),
              },
            ]}
          >
            <TextInput placeholder="Kate" maxLength={30} />
          </Form.Item>
          <Form.Item
            name={'lastName'}
            label={t('common:labels.lastName')}
            rules={[
              { required: true, message: t('common:validation.error.lastNameRequired') },
              {
                pattern: DEFAULT_NAME_FIELD_REGEX,
                message: t('common:validation.error.incorrectFormat'),
              },
            ]}
          >
            <TextInput placeholder="Olov" maxLength={30} />
          </Form.Item>
          <Form.Item
            name={'email'}
            label={t('employee:labels.email')}
            rules={[
              { required: true, message: t('employee:validationError.emailRequired') },
              { type: 'email', message: t('common:validation.error.incorrectFormat') },
            ]}
          >
            <TextInput maxLength={50} placeholder="kateonolov@gmail.com" />
          </Form.Item>
        </FieldsRow>
        <FieldsRow>
          <Form.Item
            name="jobCompanyId"
            label={t('common:labels.company')}
            rules={[{ required: true, message: t('jobs:validation.companyRequired') }]}
          >
            <SelectInput
              placeholder="Asteric.Co"
              onSelect={() => {
                handleSelect('departmentId');
                handleSelect('questionnaireSettingsId');
              }}
            >
              {companies.map(({ _id, name }: any) => (
                <SelectInput.Option key={_id} value={_id}>
                  {name}
                </SelectInput.Option>
              ))}
            </SelectInput>
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const companyId = getFieldValue('jobCompanyId');

              const company = companies.find((item: any) => item._id === companyId);

              return (
                <Form.Item
                  name="departmentId"
                  label={t('common:labels.department')}
                  rules={[
                    { required: true, message: t('common:validation.error.departmentRequired') },
                  ]}
                >
                  <SelectInput
                    disabled={!companyId}
                    placeholder="Mauris netus"
                    onSelect={id => {
                      setDepartmentId(id);
                      handleSelect('jobId');
                      handleSelect('questionnaireSettingsId');
                    }}
                  >
                    {(company ? company.departments : []).map(({ _id, name }: any) => (
                      <SelectInput.Option key={_id} value={_id}>
                        {name}
                      </SelectInput.Option>
                    ))}
                  </SelectInput>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <Form.Item
                name="jobId"
                label={t('common:labels.jobTitle')}
                rules={[{ required: true, message: t('common:validation.error.jobTitleRequired') }]}
              >
                <SelectInput
                  placeholder={t('settings:permissionGroupSettings.placeholders.builders')}
                  disabled={!getFieldValue('departmentId')}
                  onSelect={id => {
                    const jobItem = jobs.find(({ job }: any) => job?._id === id);
                    setQuestionnaireSettingIds(jobItem?.job?.questionnaireSettingsIds || []);
                    handleSelect('jobLocationId');
                    handleSelect('questionnaireSettingsId');
                  }}
                >
                  {jobs.map(({ job }: any) => (
                    <SelectInput.Option key={job._id} value={job._id}>
                      {job.title}
                    </SelectInput.Option>
                  ))}
                </SelectInput>
              </Form.Item>
            )}
          </Form.Item>
        </FieldsRow>
        <FieldsRow>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const jobId = getFieldValue('jobId');

              return (
                <Form.Item
                  name="questionnaireSettingsId"
                  label={t('employee:questionnaire.labels.questionnaireSettings')}
                  rules={[
                    {
                      required: true,
                      message: t('employee:questionnaire.validation.questionnaireSettingsRequired'),
                    },
                  ]}
                >
                  <SelectInput disabled={!jobId} placeholder="Mauris netus">
                    {(questionnaireSetting || []).map(({ _id, name }: any) => (
                      <SelectInput.Option key={_id} value={_id}>
                        {name}
                      </SelectInput.Option>
                    ))}
                  </SelectInput>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const jobId = getFieldValue('jobId');

              const jobLocations = jobs.find((item: any) => item.job._id === jobId);

              return (
                <Form.Item
                  name="jobLocationId"
                  label={t('common:labels.location')}
                  rules={[
                    { required: true, message: t('common:validation.error.locationRequired') },
                  ]}
                >
                  <SelectInput placeholder="Hamburg" disabled={!jobId}>
                    {(jobLocations ? jobLocations.job.jobLocations : []).map(
                      ({ companyLocation, _id }: any) => (
                        <SelectInput.Option key={_id} value={_id}>
                          {companyLocation.name}
                        </SelectInput.Option>
                      )
                    )}
                  </SelectInput>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item
            name="responsiblePersonsIds"
            label={t('vacancies:labels.responsiblePersons')}
            rules={[
              { required: true, message: t('vacancies:validation.responsiblePersonsRequired') },
            ]}
          >
            <CheckboxSelect
              placeholder={t('common:placeholders.selectFromTheList')}
              optionData={employees.map((item: any) => ({
                label: item.profile.displayName,
                value: item._id,
              }))}
            />
          </Form.Item>
        </FieldsRow>
      </MainWrapper>
      <ButtonsWrapper marginTop={16}>
        <Button type="primary" loading={loading} htmlType="submit">
          {t('common:buttons.save')}
        </Button>
        <Button onClick={() => navigate(ROUTES.employee)}>{t('common:buttons.cancel')}</Button>
      </ButtonsWrapper>
    </Form>
  );
};

export default EmployeeByQuestionnaireForm;
